import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Patient } from "../../basic-info/calculateBasicInfoCompletion";
import { usePatient } from "@src/packages/patients/hooks/usePatient";

export type InfertilityCause = NonNullable<
  NonNullable<
    NonNullable<Patient["fertilityHistory"]>["infertilityHistory"]
  >["causes"]
>[number]["cause"];

export interface InfertilityState {
  // From InfertilityDiagnosisScreen
  // "art_needed" = "No but I need ART for a genetic disease" (ART = Assisted Reproductive Technology)
  hasDiagnosis?: "yes" | "no" | "art_needed" | null;

  causes?: InfertilityCause[];
  causeOther?: string | null;

  // From PartnerInfertilityDiagnosisScreen
  partnerHasDiagnosis?: boolean | null;

  // Legacy cause/comments are displayed in the combined partner details field.
  partnerCause?: string | null;
  partnerComments?: string | null;
}

interface InfertilityContextType {
  state: InfertilityState;
  updateState: (updates: Partial<InfertilityState>) => void;
  resetState: () => void;
}

const InfertilityContext = createContext<InfertilityContextType | undefined>(
  undefined,
);

const initialState: InfertilityState = {
  hasDiagnosis: null,
  causes: [],
  causeOther: null,
  partnerHasDiagnosis: null,
  partnerCause: null,
  partnerComments: null,
};

export function InfertilityProvider({ children }: { children: ReactNode }) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<InfertilityState>(initialState);

  // Load data from patient (like Basic Info does)
  useEffect(() => {
    // Wait for patient data to load
    if (isLoading) {
      return;
    }

    if (!patient) {
      return;
    }

    const infertilityHistory = patient.fertilityHistory?.infertilityHistory;
    if (!infertilityHistory) {
      return;
    }

    setState((prev) => {
      // Check if state is in initial empty state - if so, always try to load
      const isInitialState =
        prev.hasDiagnosis === null &&
        (!prev.causes || prev.causes.length === 0) &&
        prev.partnerHasDiagnosis === null;

      const next: Partial<InfertilityState> = {};

      // Only update if field is empty/null (preserve user changes)
      // But if we're in initial state, always load
      if (
        isInitialState ||
        prev.hasDiagnosis === null ||
        prev.hasDiagnosis === undefined
      ) {
        if (infertilityHistory.isDiagnosedWithInfertility === true) {
          next.hasDiagnosis = "yes";
        } else if (infertilityHistory.isARTNeededForCarrierDisease === true) {
          next.hasDiagnosis = "art_needed";
        } else if (
          infertilityHistory.isDiagnosedWithInfertility === false &&
          infertilityHistory.isARTNeededForCarrierDisease === false
        ) {
          next.hasDiagnosis = "no";
        }
      }

      // Load causes ONLY if state is truly empty (initial state)
      // Once user has interacted, don't reload from DB to preserve their changes
      if (
        isInitialState &&
        infertilityHistory.causes &&
        infertilityHistory.causes.length > 0
      ) {
        next.causes = infertilityHistory.causes.map((c) => c.cause);
        // Load "other" cause details if present
        const otherCause = infertilityHistory.causes.find(
          (c) => c.cause === "other",
        );
        if (otherCause?.other) {
          next.causeOther = otherCause.other;
        }
      }

      // Load partner infertility status (only if null, or if initial state)
      if (
        isInitialState ||
        ((prev.partnerHasDiagnosis === null ||
          prev.partnerHasDiagnosis === undefined) &&
          infertilityHistory.isPartnerInfertile != null)
      ) {
        next.partnerHasDiagnosis = infertilityHistory.isPartnerInfertile;
      }

      // Load partner cause and comments (only if empty, or if initial state)
      if (
        isInitialState ||
        (!prev.partnerCause && infertilityHistory.partnerInfertilityCause)
      ) {
        next.partnerCause = infertilityHistory.partnerInfertilityCause;
      }
      if (
        isInitialState ||
        (!prev.partnerComments && infertilityHistory.notes)
      ) {
        next.partnerComments = infertilityHistory.notes;
      }

      // Only update if there are changes
      if (Object.keys(next).length === 0) {
        return prev;
      }

      const merged = { ...prev, ...next };
      // Merge with previous state (don't replace)
      return merged;
    });
  }, [patient, isLoading]); // Depend on patient and loading state

  const updateState = useCallback((updates: Partial<InfertilityState>) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <InfertilityContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </InfertilityContext.Provider>
  );
}

export function useInfertility() {
  const context = useContext(InfertilityContext);
  if (context === undefined) {
    throw new Error(
      "useInfertility must be used within an InfertilityProvider",
    );
  }
  return context;
}
